import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { qrTemplates, getTemplatesByCategory } from '../data/qrTemplates';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const TemplateGallery = ({ onSelectTemplate }) => {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [open, setOpen] = useState(false);
  const templatesByCategory = getTemplatesByCategory();

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    onSelectTemplate(template);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" data-testid="open-template-gallery-btn">
          <span className="mr-2">🎨</span>
          {selectedTemplate ? `Template: ${selectedTemplate.name}` : 'Choose from Template Gallery'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">QR Code Template Gallery</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose a pre-designed template to get started quickly
          </p>
        </DialogHeader>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-6">
            <TabsTrigger value="all">All</TabsTrigger>
            {Object.keys(templatesByCategory).slice(0, 5).map((category) => (
              <TabsTrigger key={category} value={category} className="text-xs">
                {category}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {qrTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate?.id === template.id}
                  onSelect={handleSelectTemplate}
                />
              ))}
            </div>
          </TabsContent>

          {Object.entries(templatesByCategory).map(([category, templates]) => (
            <TabsContent key={category} value={category} className="mt-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplate?.id === template.id}
                    onSelect={handleSelectTemplate}
                  />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const TemplateCard = ({ template, isSelected, onSelect }) => {
  return (
    <button
      onClick={() => onSelect(template)}
      className={`card p-4 text-left transition-all hover:scale-105 relative ${
        isSelected ? 'ring-2 ring-accent' : ''
      }`}
      data-testid={`template-${template.id}`}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 bg-accent text-white rounded-full p-1">
          <Check className="h-4 w-4" />
        </div>
      )}
      
      <div className="flex items-center justify-center mb-3">
        <div
          className="w-16 h-16 rounded-lg flex items-center justify-center text-3xl"
          style={{
            backgroundColor: template.customization.bgColor,
            border: `2px solid ${template.customization.fgColor}20`,
          }}
        >
          {template.icon}
        </div>
      </div>

      <div className="space-y-1">
        <h3 className="font-heading font-semibold text-sm text-primary">
          {template.name}
        </h3>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {template.description}
        </p>
        <div className="flex items-center gap-2 pt-2">
          <div
            className="w-4 h-4 rounded border"
            style={{ backgroundColor: template.customization.fgColor }}
          />
          <div
            className="w-4 h-4 rounded border"
            style={{ backgroundColor: template.customization.bgColor }}
          />
        </div>
      </div>
    </button>
  );
};

export default TemplateGallery;
